import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { useNavigate } from "react-router";
import { useCreateMask } from "@/hooks/useMasks";
import { Project } from "@/types/project";
import { Mask, RawMask } from "@/types/mask";
import { PcmVersion } from "@/types/pcmVersion";
import { defaultFormValues, normalizeValues, useMaskForm } from "@/hooks/useMaskForm";
import { MaskDialogContent } from "./MaskDialogContent";

type CreateMaskDialogProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  pcmVersions: PcmVersion[];
  project: Project;
  mask?: Mask;
};

export const CreateMaskDialog = ({
  onOpenChange,
  open,
  pcmVersions,
  project,
}: CreateMaskDialogProps) => {
  const { mutate } = useSWRConfig();
  const { trigger: createMask, isMutating } = useCreateMask();
  const navigate = useNavigate();

  const {
    data,
    setData,
    waferVisualizationData,
    selectedReferenceDie,
    setNewlyCreatedPcmVersion,
    pcmVersionsWithNewlyCreatedPcmVersion,
    handleChange,
    handleBlur,
    handleDieClick,
  } = useMaskForm({ open, pcmVersions });

  const handleCreateMask = async () => {
    try {
      const normalizedValues = normalizeValues(data, project);
      await createMask(normalizedValues, {
        onError: (err) => toast.error(`Error: ${err.message}`),
        onSuccess: (rawMask: RawMask) => {
          toast.success(`Successfully created Mask: "${data.name}"!`);
          mutate({ url: `/projects/${project.id}/masks` });
          setData(defaultFormValues);
          onOpenChange(false);
          navigate(`/projects/${project.id}/${rawMask.id}`);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <MaskDialogContent
      open={open}
      onOpenChange={onOpenChange}
      title="Add Mask"
      description="Add a new mask with specifications for this project."
      submitLabel="Create Mask"
      onSubmit={handleCreateMask}
      isMutating={isMutating}
      pcmVersionDisabled={false}
      data={data}
      waferVisualizationData={waferVisualizationData}
      selectedReferenceDie={selectedReferenceDie}
      pcmVersionsWithNewlyCreatedPcmVersion={pcmVersionsWithNewlyCreatedPcmVersion}
      setNewlyCreatedPcmVersion={setNewlyCreatedPcmVersion}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleDieClick={handleDieClick}
    />
  );
};
